import { useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, ExternalLinkIcon } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { AsortiGrid, type AsortiCell } from '@app/components/asorti-grid'
import { CariLink, ModelLink } from '@app/components/entity-link'
import { ProductImage } from '@app/components/product-image'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@app/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import {
  fetchStockCard,
  formatDate,
  formatDateTime,
  formatNumber,
  quantityColor,
  shortLocationName,
  stockImageKind,
  stockKeys,
  type StockBarcodeRow,
  type StockCardDetail,
  type StockColorRow,
} from '@app/features/stok/api'
import { AttributeChip, EmptyState, ErrorState, Skeleton, TableSkeleton } from '@app/features/stok/ui'
import { ApiError } from '@app/lib/api'
import { cn } from '@app/lib/cn'

/**
 * Stock card — a single page replacing Korgun's 16-tab form.
 *
 * Ordered by how often it's asked: first "how many are there" (colour × size ×
 * location matrix), then movements, then barcodes and card metadata. Korgun's
 * `OzKod1..7` pile shows up here under its real names (Kalıp, Çelikli taban,
 * Taban, Toplam desi, Astar, Sezon, Asorti) as chips.
 */

const route = getRouteApi('/_korumali/stok/$skod')

const ALL_LOCATIONS = 'hepsi'

export function StockCardScreen() {
  const { skod } = route.useParams()
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: stockKeys.card(skod),
    queryFn: () => fetchStockCard(skod),
  })

  if (isPending) return <CardSkeleton skod={skod} />

  if (isError) {
    // 404 is the server's final answer — retrying cannot conjure the card up,
    // so the only useful action is the way back to the list.
    const notFound = error instanceof ApiError && error.status === 404
    return (
      <>
        <BackLink />
        <Card>
          {notFound ? (
            <ErrorState
              title="Stok kartı bulunamadı"
              message={`${skod} kodlu stok kartı Korgun'da kayıtlı değil. Listeden doğru kartı seçebilirsiniz.`}
              action={
                <Button asChild variant="primary" size="sm">
                  <Link to="/stok">Stok listesine dön</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState
              title="Stok kartı açılamadı"
              message={error instanceof Error ? error.message : 'Kart yüklenemedi.'}
              onRetry={() => void refetch()}
            />
          )}
        </Card>
      </>
    )
  }

  return (
    <>
      <BackLink />
      <CardHeading card={data} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <OnHandMatrix card={data} />
          <Movements card={data} />
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <LocationBreakdown card={data} />
          {data.model ? <ProductionModel card={data} /> : null}
          <Barcodes card={data} />
          <CardMetadata card={data} />
        </div>
      </div>
    </>
  )
}

function BackLink() {
  return (
    <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
      <Link to="/stok">
        <ArrowLeftIcon aria-hidden />
        Stok listesi
      </Link>
    </Button>
  )
}

function CardHeading({ card }: { card: StockCardDetail }) {
  return (
    <header className="mb-4 border-b border-hairline pb-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/*
         * Beside the heading, not above it: the card's identity is the code and
         * the name, and the photo confirms them. Dropped below `sm` so it never
         * pushes the on-hand figure off a phone-width screen.
         */}
        <ProductImage
          imageKey={card.imageKey}
          alt={`${card.name ?? card.skod} fotoğrafı`}
          kind={stockImageKind(card.shape)}
          size="hero"
          className="hidden w-36 sm:block"
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-mono text-2xl font-medium tracking-tight text-ink">{card.skod}</h2>
            {card.shapeName ? <Badge tone="info">{card.shapeName}</Badge> : null}
            {card.inactive ? <Badge tone="warn">Pasif kart</Badge> : null}
            {card.critical ? <Badge tone="warn">Kritik seviyenin altında</Badge> : null}
          </div>
          <p className="mt-1 text-[0.9375rem] text-ink">{card.name ?? 'Tanım girilmemiş'}</p>
          <p className="mt-0.5 text-micro text-ink-muted">
            {[card.groupName, card.typeName, card.genderName, card.sizeGroupName]
              .filter(Boolean)
              .join(' · ') || 'Sınıflandırma girilmemiş'}
          </p>
        </div>

        <div className="text-right">
          <p className="eyebrow">Toplam mevcut</p>
          <p className={cn('font-mono text-3xl font-medium tabular-nums', quantityColor(card.total))}>
            {formatNumber(card.total)}
          </p>
          <p className="text-micro text-ink-muted">{card.unitName ?? card.unit ?? 'birim'}</p>
        </div>
      </div>

      {card.attributes.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {card.attributes.map((attribute) => (
            <AttributeChip key={attribute.label} label={attribute.label} value={attribute.value} />
          ))}
        </div>
      ) : null}
    </header>
  )
}

/** Colour × size matrix. Sizeless cards show a plain quantity instead of an asorti strip. */
function OnHandMatrix({ card }: { card: StockCardDetail }) {
  const [location, setLocation] = useState(ALL_LOCATIONS)
  const sizeless = card.sizes.length <= 1 && card.sizes[0]?.bedKod === 0

  const rows: { color: StockColorRow; total: number; cells: AsortiCell[] }[] = []
  for (const color of card.colors) {
    const source =
      location === ALL_LOCATIONS ? color : color.locations.find((loc) => loc.location === location)
    if (!source) continue
    rows.push({
      color,
      total: source.total,
      cells: source.cells.map((cell) => ({
        size: cell.size,
        qty: cell.quantity,
        status: cell.quantity < 0 ? 'warn' : undefined,
      })),
    })
  }

  return (
    <Card>
      <CardHeader className="flex-wrap gap-2">
        <CardTitle>Renk ve beden mevcudu</CardTitle>
        {card.locations.length > 1 ? (
          <div className="flex items-center gap-1" role="group" aria-label="Depo seçimi">
            <LocationButton active={location === ALL_LOCATIONS} onClick={() => setLocation(ALL_LOCATIONS)}>
              Tüm depolar
            </LocationButton>
            {card.locations.map((option) => (
              <LocationButton
                key={option.location}
                active={location === option.location}
                onClick={() => setLocation(option.location)}
              >
                {shortLocationName(option.name)}
              </LocationButton>
            ))}
          </div>
        ) : null}
      </CardHeader>

      {rows.length === 0 ? (
        <EmptyState
          title="Bu kartın stok kaydı yok"
          description="Korgun'da bu stok koduna hiç renk-beden mevcudu işlenmemiş. Kart üretime girdiğinde satırlar burada oluşur."
        />
      ) : (
        <ul className="divide-y divide-hairline">
          {rows.map(({ color, total, cells }) => (
            <li
              key={color.rkod}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3.5 py-2.5"
            >
              {/* The colourway's own photo (S_DetPicture), which is the one
                  thing the RKod cannot tell anybody apart by. */}
              <div className="flex min-w-40 items-center gap-2.5">
                <ProductImage
                  imageKey={color.imageKey}
                  alt={`${card.name ?? card.skod} · ${color.color ?? `renk ${color.rkod}`} fotoğrafı`}
                  kind={stockImageKind(card.shape)}
                />
                <div className="min-w-0">
                  <p className="text-data font-medium text-ink">
                    {color.color ?? `Renk ${color.rkod}`}
                  </p>
                  <p className="font-mono text-micro text-ink-muted">RKod {color.rkod}</p>
                </div>
              </div>

              {sizeless ? (
                <p className={cn('font-mono text-lg tabular-nums', quantityColor(total))}>
                  {formatNumber(total)}{' '}
                  <span className="text-micro text-ink-muted">{card.unit ?? ''}</span>
                </p>
              ) : (
                <AsortiGrid cells={cells} total={total} density="sm" />
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function LocationButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-xs border px-2 py-0.5 text-micro transition-colors',
        active
          ? 'border-kordovan/25 bg-kordovan/8 font-medium text-kordovan'
          : 'border-hairline bg-surface text-ink-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  )
}

function LocationBreakdown({ card }: { card: StockCardDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Depo dağılımı</CardTitle>
      </CardHeader>
      {card.locations.length === 0 ? (
        <CardContent className="text-data text-ink-muted">Depo kaydı yok.</CardContent>
      ) : (
        <TableScroller>
          <Table>
            <TableBody>
              {card.locations.map((location) => (
                <TableRow key={location.location}>
                  <TableCell className="font-mono text-ink-muted">{location.location}</TableCell>
                  <TableCell>{location.name}</TableCell>
                  <TableNumCell className={quantityColor(location.quantity)}>
                    {formatNumber(location.quantity)}
                  </TableNumCell>
                </TableRow>
              ))}
              <TableRow className="bg-kagit">
                <TableCell colSpan={2} className="font-medium">
                  Toplam
                </TableCell>
                <TableNumCell className={cn('font-medium', quantityColor(card.total))}>
                  {formatNumber(card.total)}
                </TableNumCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableScroller>
      )}
    </Card>
  )
}

function Movements({ card }: { card: StockCardDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Son hareketler</CardTitle>
        <span className="text-micro text-ink-muted">Son {card.movements.length} kayıt</span>
      </CardHeader>

      {card.movements.length === 0 ? (
        <EmptyState
          title="Hareket yok"
          description="Bu kart için henüz stok fişi ya da fatura satırı işlenmemiş."
        />
      ) : (
        <TableScroller className="max-h-96">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Tarih</TableHead>
                <TableHead className="w-40">Fiş</TableHead>
                <TableHead className="w-44">Renk</TableHead>
                <TableHead className="w-16">Beden</TableHead>
                <TableHead className="hidden w-32 md:table-cell">Depo</TableHead>
                <TableHead className="hidden min-w-36 lg:table-cell">Cari / belge</TableHead>
                <TableHead className="w-28 text-right">Miktar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {card.movements.map((movement) => (
                <TableRow key={movement.id}>
                  <TableCell className="whitespace-nowrap font-mono text-ink-muted">
                    {formatDateTime(movement.fisDate)}
                  </TableCell>
                  <TableCell>
                    {movement.fisName}
                    {movement.fisNo ? (
                      <span className="ml-1 font-mono text-micro text-ink-muted">
                        #{movement.fisNo}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="max-w-0 truncate" title={movement.color ?? undefined}>
                    {movement.color ?? '—'}
                  </TableCell>
                  <TableCell className="font-mono">{movement.size ?? '—'}</TableCell>
                  <TableCell className="hidden whitespace-nowrap text-ink-muted md:table-cell">
                    {shortLocationName(movement.locationName)}
                  </TableCell>
                  {/* The cari behind a movement is the question this column
                      always leads to ("who sent these?") — so it opens the
                      account instead of just naming it. */}
                  <TableCell
                    className="hidden max-w-0 truncate lg:table-cell"
                    title={movement.cariName ?? undefined}
                  >
                    {movement.cariKod ? (
                      <CariLink
                        ckod={movement.cariKod}
                        title={`${movement.cariName ?? movement.cariKod} cari kartını aç`}
                      >
                        {movement.cariName ?? movement.cariKod}
                      </CariLink>
                    ) : (
                      (movement.cariName ?? movement.documentNo ?? '—')
                    )}
                  </TableCell>
                  <TableNumCell
                    className={movement.direction === 'out' ? 'text-hardal' : 'text-zeytin'}
                  >
                    {movement.quantity > 0 ? '+' : ''}
                    {formatNumber(movement.quantity)}
                  </TableNumCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroller>
      )}
    </Card>
  )
}

function Barcodes({ card }: { card: StockCardDetail }) {
  const { stock, stockTotal, asorti, asortiTotal } = card.barcodes

  return (
    <Card>
      <CardHeader>
        <CardTitle>Barkodlar</CardTitle>
        <span className="text-micro text-ink-muted tabular-nums">
          {formatNumber(stockTotal)} tekil · {formatNumber(asortiTotal)} asorti
        </span>
      </CardHeader>

      {stockTotal === 0 && asortiTotal === 0 ? (
        <CardContent className="text-data text-ink-muted">
          Bu karta barkod tanımlanmamış.
        </CardContent>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          {stock.length > 0 ? (
            <BarcodeList
              title="Tekil barkodlar"
              shown={stock.length}
              total={stockTotal}
              rows={stock}
              secondColumn={(row) => row.size ?? '—'}
            />
          ) : null}
          {asorti.length > 0 ? (
            <BarcodeList
              title="Asorti barkodları"
              shown={asorti.length}
              total={asortiTotal}
              rows={asorti}
              secondColumn={(row) => row.asortiName ?? row.asorti ?? '—'}
            />
          ) : null}
        </div>
      )}
    </Card>
  )
}

function BarcodeList({
  title,
  shown,
  total,
  rows,
  secondColumn,
}: {
  title: string
  shown: number
  total: number
  rows: StockBarcodeRow[]
  secondColumn: (row: StockBarcodeRow) => string
}) {
  return (
    <section>
      <h4 className="eyebrow sticky top-0 border-b border-hairline bg-kagit px-3.5 py-1.5">
        {title}
        {shown < total ? ` · ${formatNumber(shown)} / ${formatNumber(total)}` : ''}
      </h4>
      <ul className="divide-y divide-hairline">
        {rows.map((row) => (
          <li key={row.barcode} className="flex items-baseline gap-2 px-3.5 py-1.5 text-data">
            <span className="shrink-0 font-mono text-ink">{row.barcode}</span>
            <span className="min-w-12 flex-1 truncate text-ink-muted">
              {row.color ?? `Renk ${row.rkod ?? '—'}`}
            </span>
            <span
              className="max-w-[55%] shrink truncate font-mono text-ink-muted"
              title={secondColumn(row)}
            >
              {secondColumn(row)}
            </span>
            {row.active ? null : <Badge tone="warn">Pasif</Badge>}
          </li>
        ))}
      </ul>
    </section>
  )
}

function ProductionModel({ card }: { card: StockCardDetail }) {
  const model = card.model
  if (!model) return null
  return (
    <Card>
      <CardHeader>
        <CardTitle>Üretim modeli</CardTitle>
      </CardHeader>
      <CardContent className="flex items-baseline justify-between gap-3">
        <span>
          <ModelLink modelKod={model.modelKod} className="font-mono text-data" />
          {model.name && model.name !== model.modelKod ? (
            <span className="ml-2 text-data text-ink-muted">{model.name}</span>
          ) : null}
          <span className="mt-0.5 block text-micro text-ink-muted">
            Reçete {model.location ?? '—'} deposundaki malzemelere göre çözülür.
          </span>
        </span>
        {/* Straight to this card's own model, not the model list — the list was
            one search away from the answer the user is already holding. */}
        <Button asChild variant="outline" size="sm">
          <Link to="/modeller/$modelKod" params={{ modelKod: model.modelKod }}>
            Model kartı
            <ExternalLinkIcon aria-hidden />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function CardMetadata({ card }: { card: StockCardDetail }) {
  const rows: [string, string][] = [
    ['Stok grubu', formatLabel(card.groupName, card.groupCode)],
    ['Stok tipi', formatLabel(card.typeName, card.typeCode)],
    ['Cinsiyet', formatLabel(card.genderName, card.genderCode)],
    ['Birim', formatLabel(card.unitName, card.unit)],
    ['Beden grubu', formatLabel(card.sizeGroupName, card.sizeGroup)],
    ['Marka', card.brand ?? '—'],
    ['Üretici kodu', card.manufacturerCode ?? '—'],
    ['Reyon', card.department ?? '—'],
    ['EAN barkod', card.eanCode ?? '—'],
    ['KDV / ÖTV', `%${formatNumber(card.vatRate ?? 0)} / %${formatNumber(card.exciseRate ?? 0)}`],
    ['Kritik / optimal seviye', `${formatNumber(card.criticalLevel)} / ${formatNumber(card.optimalLevel)}`],
    ['Kart tarihi', formatDate(card.cardDate)],
    ['Son düzenleme', formatDateTime(card.lastUpdated)],
    [
      'İzleme',
      [card.lotTracked ? 'Lot' : null, card.serialTracked ? 'Seri' : null, card.reservable ? 'Rezerv' : null]
        .filter(Boolean)
        .join(' · ') || 'Yok',
    ],
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Kart künyesi</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-data">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-ink-muted">{label}</dt>
              <dd className="truncate text-right text-ink" title={value}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
        {card.note ? (
          <p className="mt-3 border-t border-hairline pt-2 text-data text-ink-muted">{card.note}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

function formatLabel(name: string | null, code: string | null): string {
  if (name && code && name !== code) return `${name} (${code})`
  return name ?? code ?? '—'
}

function CardSkeleton({ skod }: { skod: string }) {
  return (
    <>
      <BackLink />
      <header className="mb-4 border-b border-hairline pb-4">
        <p className="font-mono text-2xl font-medium tracking-tight text-ink">{skod}</p>
        <Skeleton className="mt-2 h-3.5 w-64" />
        <Skeleton className="mt-2 h-3 w-40" />
      </header>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card>
          <TableSkeleton rows={6} columns={3} />
        </Card>
        <Card>
          <TableSkeleton rows={4} columns={2} />
        </Card>
      </div>
    </>
  )
}
